// DOM renderer for the web tour. Welcome shows as a centered card; the other
// steps dim the page with an SVG mask and cut a rounded hole around the live
// target element, with a popover card placed on whichever side has room -
// geometry-based placement so RTL needs no special casing.
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../hooks/useTheme';
import type { TourStep } from './types';
import {
  getComposerTargetElement,
  getMobileMenuElement,
  getSidebarCaptionElement,
  type WebSidebarCaption,
} from './web-tour-targets';

const HOLE_PADDING = 8;
const HOLE_RADIUS = 14;
const POPOVER_GAP = 14;
const VIEWPORT_MARGIN = 12;
const MEASURE_INTERVAL_MS = 120;
// Give slide-in transitions (mobile drawer) and route paints time to settle
// before giving up on a target and auto-advancing past its step.
const MISSING_TARGET_TIMEOUT_MS = 2400;

type TargetRect = { x: number; y: number; width: number; height: number };

// Tabbio brand sparkle asset for the welcome card. Loaded via require +
// uri/default unwrap, mirroring UpgradeHeroIcon.web's asset handling.
const SPARK_IMG = require('../../../assets/branding/upgrade-spark.png');
const SPARK_SRC: string =
  typeof SPARK_IMG === 'string'
    ? SPARK_IMG
    : (SPARK_IMG as { uri?: string; default?: string } | null)?.uri ??
      (SPARK_IMG as { default?: string } | null)?.default ??
      '';

export interface TourOverlayProps {
  step: Exclude<TourStep, 'complete'>;
  captions: WebSidebarCaption[];
  captionIndex: number;
  onNext: () => void;
  onSkip: () => void;
}

function rectsDiffer(a: TargetRect | null, b: TargetRect | null) {
  if (!a || !b) return a !== b;
  return (
    Math.abs(a.x - b.x) > 0.5 ||
    Math.abs(a.y - b.y) > 0.5 ||
    Math.abs(a.width - b.width) > 0.5 ||
    Math.abs(a.height - b.height) > 0.5
  );
}

export function TourOverlay({
  step,
  captions,
  captionIndex,
  onNext,
  onSkip,
}: TourOverlayProps) {
  const { t } = useTranslation('tour');
  const { isRTL } = useLocale();
  const { isDark } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined'
      ? { width: 0, height: 0 }
      : { width: window.innerWidth, height: window.innerHeight },
  );
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const primaryButtonRef = useRef<HTMLButtonElement | null>(null);
  const [popoverSize, setPopoverSize] = useState({ width: 320, height: 160 });
  const targetRectRef = useRef<TargetRect | null>(null);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const onSkipRef = useRef(onSkip);
  onSkipRef.current = onSkip;

  const caption = captions[Math.min(captionIndex, captions.length - 1)];

  const resolveTarget = useCallback((): HTMLElement | null => {
    if (step === 'chat') return getComposerTargetElement();
    if (step === 'drawer-icon') return getMobileMenuElement();
    if (step === 'drawer-items') {
      return caption ? getSidebarCaptionElement(caption) : null;
    }
    return null;
  }, [caption, step]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Track the target's rect while the step is active: measure now, then on
  // resize/scroll and on a light interval to follow slide-in transitions.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (step === 'welcome') {
      targetRectRef.current = null;
      setTargetRect(null);
      return undefined;
    }

    let missingSince: number | null = null;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      const el = resolveTarget();
      if (!el) {
        if (missingSince === null) {
          missingSince = Date.now();
        } else if (Date.now() - missingSince > MISSING_TARGET_TIMEOUT_MS) {
          // Target never appeared (hidden layout variant): don't strand the
          // user behind a dim overlay with no hole; move on.
          missingSince = null;
          onNextRef.current();
        }
        return;
      }
      missingSince = null;
      const rect = el.getBoundingClientRect();
      const next: TargetRect = {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      };
      if (rectsDiffer(targetRectRef.current, next)) {
        targetRectRef.current = next;
        setTargetRect(next);
      }
    };

    targetRectRef.current = null;
    setTargetRect(null);
    measure();
    const interval = window.setInterval(measure, MEASURE_INTERVAL_MS);
    const onViewportChange = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      measure();
    };
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', measure, true);
    };
  }, [resolveTarget, step]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onSkipRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useLayoutEffect(() => {
    const node = popoverRef.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();
    if (
      Math.abs(width - popoverSize.width) > 0.5 ||
      Math.abs(height - popoverSize.height) > 0.5
    ) {
      setPopoverSize({ width, height });
    }
  }, [step, captionIndex, targetRect, popoverSize.width, popoverSize.height]);

  useEffect(() => {
    primaryButtonRef.current?.focus({ preventScroll: true });
  }, [step, captionIndex]);

  if (!mounted || typeof document === 'undefined') return null;

  const dimColor = isDark ? 'rgba(0, 0, 0, 0.62)' : 'rgba(0, 0, 0, 0.42)';

  const isLastCaption = captionIndex >= captions.length - 1;
  // The very last stop of the walk (final sidebar caption). Its primary CTA
  // already completes the tour, so the redundant "Skip tour" affordance is
  // dropped there.
  const isFinalStep = step === 'drawer-items' && isLastCaption;
  const copy =
    step === 'chat'
      ? {
          title: t('chat.title'),
          body: t('chat.body'),
          primaryCta: t('chat.primaryCta'),
        }
      : step === 'drawer-icon'
        ? {
            title: t('drawerIcon.title'),
            body: t('drawerIcon.body'),
            primaryCta: t('drawerIcon.primaryCta'),
          }
        : step === 'drawer-items' && caption
          ? {
              title: t('drawerItems.title'),
              body: t(`drawerItems.${caption.key}`),
              primaryCta: isLastCaption
                ? t('chat.primaryCta')
                : t('drawerIcon.primaryCta'),
            }
          : null;

  const hole = targetRect
    ? {
        x: targetRect.x - HOLE_PADDING,
        y: targetRect.y - HOLE_PADDING,
        width: targetRect.width + HOLE_PADDING * 2,
        height: targetRect.height + HOLE_PADDING * 2,
      }
    : null;

  // Popover placement: wide targets (the composer) get above/below; narrow
  // ones (nav rows, hamburger) sit beside the hole on the roomier side.
  let popoverLeft = 0;
  let popoverTop = 0;
  if (hole) {
    const isWideTarget = hole.width > viewport.width * 0.6;
    if (isWideTarget) {
      const roomAbove = hole.y - VIEWPORT_MARGIN;
      popoverTop =
        roomAbove >= popoverSize.height + POPOVER_GAP
          ? hole.y - POPOVER_GAP - popoverSize.height
          : hole.y + hole.height + POPOVER_GAP;
      popoverLeft = hole.x + hole.width / 2 - popoverSize.width / 2;
    } else {
      const targetCenterX = hole.x + hole.width / 2;
      const preferEast = targetCenterX < viewport.width / 2;
      const eastLeft = hole.x + hole.width + POPOVER_GAP;
      const westLeft = hole.x - POPOVER_GAP - popoverSize.width;
      const eastFits =
        eastLeft + popoverSize.width <= viewport.width - VIEWPORT_MARGIN;
      const westFits = westLeft >= VIEWPORT_MARGIN;
      if (preferEast && eastFits) popoverLeft = eastLeft;
      else if (!preferEast && westFits) popoverLeft = westLeft;
      else if (eastFits) popoverLeft = eastLeft;
      else if (westFits) popoverLeft = westLeft;
      else popoverLeft = viewport.width / 2 - popoverSize.width / 2;
      if (eastFits || westFits) {
        popoverTop = hole.y + hole.height / 2 - popoverSize.height / 2;
      } else {
        // Neither side fits (narrow viewport, e.g. the mobile drawer walk):
        // drop below the hole so the card never covers the spotlit row.
        const belowTop = hole.y + hole.height + POPOVER_GAP;
        popoverTop =
          belowTop + popoverSize.height <= viewport.height - VIEWPORT_MARGIN
            ? belowTop
            : hole.y - POPOVER_GAP - popoverSize.height;
      }
    }
    popoverLeft = Math.min(
      Math.max(popoverLeft, VIEWPORT_MARGIN),
      viewport.width - popoverSize.width - VIEWPORT_MARGIN,
    );
    popoverTop = Math.min(
      Math.max(popoverTop, VIEWPORT_MARGIN),
      viewport.height - popoverSize.height - VIEWPORT_MARGIN,
    );
  } else {
    // Target not measurable yet (route/paint still settling): center the card
    // so the step copy shows instantly instead of a blank dim. The 200ms
    // left/top transition slides it to its anchor once the hole resolves.
    popoverLeft = Math.max(
      viewport.width / 2 - popoverSize.width / 2,
      VIEWPORT_MARGIN,
    );
    popoverTop = Math.max(
      viewport.height / 2 - popoverSize.height / 2,
      VIEWPORT_MARGIN,
    );
  }

  const primaryButtonStyle: React.CSSProperties = {
    width: '100%',
    minHeight: 46,
    borderRadius: 999,
    border: 0,
    backgroundColor: 'var(--tabbio-text)',
    color: 'var(--tabbio-bg)',
    font: 'inherit',
    fontSize: 15,
    fontWeight: 650,
    cursor: 'pointer',
  };
  const skipButtonStyle: React.CSSProperties = {
    marginTop: 4,
    minHeight: 34,
    border: 0,
    background: 'transparent',
    color: 'var(--tabbio-text-secondary)',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 550,
    cursor: 'pointer',
  };

  const overlay = (
    <div
      className="tabbio-tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('welcome.title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {step === 'welcome' ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            backgroundColor: dimColor,
          }}
        >
          <section
            className="tabbio-tour-card"
            style={{
              width: '100%',
              maxWidth: 420,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              padding: '34px 28px 24px',
              borderRadius: 32,
              backgroundColor: 'var(--tabbio-bg)',
              color: 'var(--tabbio-text)',
              boxShadow: '0 24px 70px rgba(0, 0, 0, 0.22)',
              textAlign: 'center',
            }}
          >
            <div
              aria-hidden
              style={{
                width: 60,
                height: 60,
                borderRadius: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--tabbio-surface-secondary)',
              }}
            >
              <img
                src={SPARK_SRC}
                alt=""
                width={40}
                height={40}
                style={{ display: 'block', objectFit: 'contain' }}
              />
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 26,
                lineHeight: '34px',
                fontWeight: 680,
                letterSpacing: -0.5,
              }}
            >
              {t('welcome.title')}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 16,
                lineHeight: '24px',
                color: 'var(--tabbio-text-secondary)',
              }}
            >
              {t('welcome.body')}
            </p>
            <button
              ref={primaryButtonRef}
              type="button"
              onClick={onNext}
              style={{ ...primaryButtonStyle, marginTop: 10, minHeight: 54, fontSize: 16 }}
            >
              {t('welcome.primaryCta')}
            </button>
            <button type="button" onClick={onSkip} style={skipButtonStyle}>
              {t('welcome.skipCta')}
            </button>
          </section>
        </div>
      ) : (
        <>
          <svg
            width="100%"
            height="100%"
            style={{ position: 'absolute', inset: 0 }}
            aria-hidden
          >
            <defs>
              <mask id="tabbio-tour-mask">
                <rect width="100%" height="100%" fill="#fff" />
                {hole ? (
                  <rect
                    x={hole.x}
                    y={hole.y}
                    width={hole.width}
                    height={hole.height}
                    rx={HOLE_RADIUS}
                    ry={HOLE_RADIUS}
                    fill="#000"
                  />
                ) : null}
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill={dimColor}
              mask="url(#tabbio-tour-mask)"
            />
            {hole ? (
              <rect
                x={hole.x}
                y={hole.y}
                width={hole.width}
                height={hole.height}
                rx={HOLE_RADIUS}
                ry={HOLE_RADIUS}
                fill="none"
                stroke={isDark ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.9)'}
                strokeWidth={2}
              />
            ) : null}
          </svg>

          {copy ? (
            <div
              ref={popoverRef}
              className="tabbio-tour-card"
              style={{
                position: 'absolute',
                left: popoverLeft,
                top: popoverTop,
                width: 320,
                maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '18px 20px 14px',
                borderRadius: 20,
                backgroundColor: 'var(--tabbio-bg)',
                color: 'var(--tabbio-text)',
                boxShadow: '0 18px 50px rgba(0, 0, 0, 0.28)',
                transition: 'left 200ms ease, top 200ms ease',
              }}
            >
              <h2 style={{ margin: 0, fontSize: 17, lineHeight: '23px', fontWeight: 680 }}>
                {copy.title}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: 14.5,
                  lineHeight: '21px',
                  color: 'var(--tabbio-text-secondary)',
                }}
              >
                {copy.body}
              </p>
              {step === 'drawer-items' && captions.length > 1 ? (
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--tabbio-text-tertiary, var(--tabbio-text-secondary))',
                  }}
                >
                  {captionIndex + 1} / {captions.length}
                </span>
              ) : null}
              <button
                ref={primaryButtonRef}
                type="button"
                onClick={onNext}
                style={{ ...primaryButtonStyle, marginTop: 6 }}
              >
                {copy.primaryCta}
              </button>
              {isFinalStep ? null : (
                <button type="button" onClick={onSkip} style={skipButtonStyle}>
                  {t('welcome.skipCta')}
                </button>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
